import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { PoEditLine } from '@/api/onderaannemersPoEdit';
import { reorderPoLines } from '@/api/onderaannemersPoEdit';
import { PoEditLineRow, type LineSavePatch } from './PoEditLineRow';
import { moveBlock, toOrderedIds, type ReorderableLine } from './reorderLogic';

interface PoEditLineListProps {
  poId: number;
  lines: PoEditLine[];
  // Product ids for which the vendor prices BOTH tiers → the inline editor may offer the
  // staffel switch. Keyed by productId.
  tierSwitchableProductIds: Set<number>;
  // Replace the working line order optimistically (used for reorder + revert).
  onOptimisticReorder: (lines: PoEditLine[]) => void;
  onSaveLine: (lineId: number, patch: LineSavePatch) => Promise<void>;
  onDeleteLine: (line: PoEditLine) => void;
  onReorderFailed: (message: string) => void;
}

export function PoEditLineList({
  poId,
  lines,
  tierSwitchableProductIds,
  onOptimisticReorder,
  onSaveLine,
  onDeleteLine,
  onReorderFailed,
}: PoEditLineListProps) {
  const [activeId, setActiveId] = useState<number | null>(null);
  const [savingLineId, setSavingLineId] = useState<number | null>(null);
  const [reordering, setReordering] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const sortableIds = useMemo(() => lines.map((l) => l.id), [lines]);
  const activeLine = activeId != null ? lines.find((l) => l.id === activeId) ?? null : null;

  // Grabbing-hand cursor across the document while dragging (EasyKit pattern).
  useEffect(() => {
    if (activeId === null) return;
    const styleEl = document.createElement('style');
    styleEl.textContent = `*, *::before, *::after { cursor: grabbing !important; }`;
    document.head.appendChild(styleEl);
    return () => { document.head.removeChild(styleEl); };
  }, [activeId]);

  const handleDragStart = useCallback((e: DragStartEvent) => {
    setActiveId(Number(e.active.id));
  }, []);

  const handleDragEnd = useCallback(
    async (e: DragEndEvent) => {
      setActiveId(null);
      const { active, over } = e;
      if (!over || active.id === over.id) return;

      const activeLineId = Number(active.id);
      const overLineId = Number(over.id);

      const before = lines;
      const reorderable: ReorderableLine[] = before.map((l) => ({
        id: l.id,
        isSection: l.displayType === 'line_section',
      }));
      const nextReorderable = moveBlock(reorderable, activeLineId, overLineId);
      const orderedIds = toOrderedIds(nextReorderable);

      // No change (dropped inside its own block, etc.) → skip the round-trip.
      if (orderedIds.every((id, i) => id === before[i].id)) return;

      const byId = new Map(before.map((l) => [l.id, l]));
      const nextLines = orderedIds.map((id) => byId.get(id)!).filter(Boolean);

      // Optimistic apply, then persist; revert on failure (EasyKit saveInFlight pattern).
      onOptimisticReorder(nextLines);
      setReordering(true);
      try {
        await reorderPoLines(poId, orderedIds);
      } catch (err) {
        onOptimisticReorder(before); // revert
        onReorderFailed(
          err instanceof Error ? err.message : 'Volgorde opslaan mislukt — probeer opnieuw.',
        );
      } finally {
        setReordering(false);
      }
    },
    [lines, poId, onOptimisticReorder, onReorderFailed],
  );

  const handleSave = useCallback(
    async (lineId: number, patch: LineSavePatch) => {
      setSavingLineId(lineId);
      try {
        await onSaveLine(lineId, patch);
      } finally {
        setSavingLineId(null);
      }
    },
    [onSaveLine],
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className={reordering ? 'opacity-70 pointer-events-none' : ''}>
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          {lines.map((line) => (
            <PoEditLineRow
              key={line.id}
              line={line}
              tierSwitchable={line.productId != null && tierSwitchableProductIds.has(line.productId)}
              saving={savingLineId === line.id}
              onSave={(patch) => handleSave(line.id, patch)}
              onDelete={() => onDeleteLine(line)}
            />
          ))}
        </SortableContext>
      </div>

      <DragOverlay dropAnimation={null}>
        {activeLine ? (
          <div className="px-2 py-1.5 rounded bg-white shadow-lg border border-td-blue/40 text-sm text-td-navy">
            {activeLine.displayType === 'line_section' ? (
              <span className="font-semibold">{activeLine.label}</span>
            ) : (
              activeLine.label
            )}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
